import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon, Kbd } from '@one/ui';

export interface Befehl {
  id: string;
  titel: string;
  /** Gruppe in der Palette, z. B. „Module", „Seiten", „Mandant". */
  bereich: string;
  /** Herkunft rechts in der Zeile, damit gleiche Namen unterscheidbar bleiben. */
  wo?: string;
  icon?: string;
  /** Eigenes Modul-Symbol (siehe ManifestEntry.iconKnoten); Vorrang vor `icon`. */
  iconKnoten?: unknown;
  ausfuehren: () => void;
}

/**
 * Die Befehlspalette (Designsystem „CommandPalette", Konzept Kap. 02).
 *
 * Zweck ist nicht Schnelligkeit für Vielnutzer, sondern **Auffindbarkeit**: In
 * einer Plattform mit vielen Modulen weiß niemand, wo etwas liegt. Wer `Strg+K`
 * drückt und „Freischaltung" tippt, muss nicht wissen, dass das in der Verwaltung
 * unter einem Reiter steht.
 *
 * Die Liste kommt von außen — die Palette weiß nichts über Module, Rechte oder
 * Mandanten. Was jemand nicht darf, steht gar nicht erst darin.
 */
const OEFFNEN = 'one:befehlspalette';

/**
 * Öffnet die Palette von außen — für das Suchfeld der Topbar und die Rail.
 * Über ein Ereignis und nicht über eine Prop, weil die Palette am Ende des Baums
 * hängt: Ein gemeinsamer Zustand würde die halbe Shell neu rendern.
 */
export function befehlspaletteOeffnen(): void {
  window.dispatchEvent(new CustomEvent(OEFFNEN));
}

/** Höchstens fünf Treffer je Gruppe (Designsystem). */
const JE_GRUPPE = 5;

export function Befehlspalette({ befehle }: { befehle: Befehl[] }) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [markiert, setMarkiert] = useState(0);
  const eingabe = useRef<HTMLInputElement>(null);
  const vorherFokus = useRef<HTMLElement | null>(null);
  const listId = useId();

  useEffect(() => {
    function oeffnen() {
      vorherFokus.current = document.activeElement as HTMLElement | null;
      setOffen(true);
      setSuche('');
      setMarkiert(0);
    }
    function taste(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOffen((o) => {
          if (!o) vorherFokus.current = document.activeElement as HTMLElement | null;
          return !o;
        });
        setSuche('');
        setMarkiert(0);
      }
      if (e.key === 'Escape') setOffen(false);
    }
    window.addEventListener('keydown', taste);
    window.addEventListener(OEFFNEN, oeffnen);
    return () => {
      window.removeEventListener('keydown', taste);
      window.removeEventListener(OEFFNEN, oeffnen);
    };
  }, []);

  useEffect(() => {
    if (offen) eingabe.current?.focus();
    else vorherFokus.current?.focus?.();
  }, [offen]);

  const gruppen = useMemo(() => {
    const begriff = suche.trim().toLowerCase();
    const passend = begriff
      ? befehle.filter((b) =>
          `${b.titel} ${b.bereich} ${b.wo ?? ''}`.toLowerCase().includes(begriff),
        )
      : befehle;
    const nachBereich = new Map<string, Befehl[]>();
    for (const b of passend) {
      const liste = nachBereich.get(b.bereich) ?? [];
      if (liste.length < JE_GRUPPE) liste.push(b);
      nachBereich.set(b.bereich, liste);
    }
    return [...nachBereich.entries()];
  }, [suche, befehle]);

  const flach = gruppen.flatMap(([, liste]) => liste);

  if (!offen) return null;

  function waehlen(index: number) {
    const befehl = flach[index];
    if (!befehl) return;
    setOffen(false);
    befehl.ausfuehren();
  }

  let laufend = -1;
  return (
    <div
      className="one-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && setOffen(false)}
    >
      <div className="one-palette" role="dialog" aria-modal="true" aria-label="Befehlspalette">
        <div className="one-palette-input">
          <Icon name="search" />
          <input
            ref={eingabe}
            value={suche}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={flach[markiert] ? `${listId}-${markiert}` : undefined}
            placeholder="Wohin? Modul, Seite oder Mandant"
            onChange={(e) => {
              setSuche(e.target.value);
              setMarkiert(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMarkiert((m) => Math.min(m + 1, flach.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMarkiert((m) => Math.max(m - 1, 0));
              }
              if (e.key === 'Enter') waehlen(markiert);
            }}
          />
          <Kbd>ESC</Kbd>
        </div>
        <div className="one-palette-list" id={listId} role="listbox" aria-label="Treffer">
          {flach.length === 0 ? (
            <div className="one-empty">
              <Icon name="search" />
              <div className="one-empty-title">Nichts gefunden</div>
              <div>Versuch es mit dem Namen eines Moduls oder Mandanten.</div>
            </div>
          ) : (
            gruppen.map(([bereich, liste]) => (
              <div key={bereich} role="group" aria-label={bereich}>
                <div className="one-palette-group">
                  <span className="one-overline">{bereich}</span>
                </div>
                {liste.map((b) => {
                  laufend += 1;
                  const i = laufend;
                  return (
                    <button
                      key={b.id}
                      id={`${listId}-${i}`}
                      type="button"
                      role="option"
                      aria-selected={i === markiert}
                      className="one-palette-item"
                      data-active={i === markiert ? 'true' : undefined}
                      onMouseEnter={() => setMarkiert(i)}
                      onClick={() => waehlen(i)}
                    >
                      <Icon name={b.icon ?? 'arrow-right'} knoten={b.iconKnoten} />
                      <span>{b.titel}</span>
                      {b.wo && <span className="where">{b.wo}</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
